import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Flex, Spinner, Text } from '@optiaxiom/react';

import { useNodeOperations } from '../../../../hooks/useNodeOperations';
import { useWorkflow } from '../../../../hooks/useWorkflow';
import { NodeParameterValueType } from '../../../../types/node-properties';
import { ParameterManager } from '../../../parameters/ParameterManager';
import { PropertyRenderer } from '../../../parameters/ParameterRenderer';

import { formContainerStyle, formContentStyle } from './NodePropertyForm.css';

type NodePropertyFormProps = {
  nodeId: string;
  nodeType: string;
  initialData: Record<string, unknown>;
  onParameterChange?: (data: Record<string, unknown>) => void;
};

export type NodePropertyFormRef = {
  getFormData: () => {
    parameters: Record<string, NodeParameterValueType>;
    errors: Record<string, string>;
    isDirty: boolean;
    nodeOperations: ReturnType<typeof useNodeOperations>;
    nodeTypeDefinition: ReturnType<ReturnType<typeof useWorkflow>['getNodeConfig']>;
    parameterManager: ParameterManager;
  };
  validateAndTransform: () => {
    isValid: boolean;
    transformed?: Record<string, unknown>;
    errors: Record<string, string>;
  };
  clearErrors: () => void;
  setIsDirty: (dirty: boolean) => void;
};

export const NodePropertyForm = forwardRef<NodePropertyFormRef, NodePropertyFormProps>(
  ({ nodeId, nodeType, initialData, onParameterChange }, ref) => {
    const { getNodeConfig } = useWorkflow();
    const nodeOperations = useNodeOperations({
      nodeId,
      nodeType,
      nodeData: initialData as Record<string, unknown>,
    });

    // State for form parameters - merge initial data with fetched data
    const [parameters, setParameters] = useState<Record<string, NodeParameterValueType>>(() => ({
      ...(initialData as Record<string, NodeParameterValueType>),
      ...(nodeOperations.data as Record<string, NodeParameterValueType>),
    }));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isDirty, setIsDirty] = useState<boolean>(false);

    // Track when we just completed a save to avoid immediate reset
    const justSavedRef = useRef<boolean>(false);

    const nodeTypeDefinition = getNodeConfig(nodeType);
    const parameterManager = useMemo(() => new ParameterManager(), []);
    const properties = useMemo(
      () => nodeTypeDefinition?.description.properties || [],
      [nodeTypeDefinition]
    );

    // Update parameters when initial data changes or when React Query data is fetched
    useEffect(() => {
      if (isDirty) return; // Avoid clobbering user edits while typing
      if (justSavedRef.current) {
        // Don't reset immediately after save - let the saved data persist
        justSavedRef.current = false;
        return;
      }
      const mergedData = {
        ...(initialData as Record<string, NodeParameterValueType>),
        ...(nodeOperations.data as Record<string, NodeParameterValueType>),
      };
      setParameters(mergedData);
    }, [initialData, nodeOperations.data, isDirty]);

    // Handle React Query errors
    useEffect(() => {
      if (nodeOperations.error) {
        setErrors({ general: 'Failed to load data' });
      }
      if (nodeOperations.saveError) {
        setErrors(prev => ({
          ...prev,
          general: nodeOperations.saveError?.message || 'Failed to save changes',
        }));
      }
      if (nodeOperations.deleteError) {
        setErrors(prev => ({
          ...prev,
          general: nodeOperations.deleteError?.message || 'Failed to delete',
        }));
      }
    }, [nodeOperations.error, nodeOperations.saveError, nodeOperations.deleteError]);

    // Expose methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        getFormData: () => ({
          parameters,
          errors,
          isDirty,
          nodeOperations,
          nodeTypeDefinition,
          parameterManager,
        }),
        validateAndTransform: () => {
          // Validate parameters
          const validationResult = parameterManager.validateParameters(
            parameters,
            properties,
            nodeType
          );

          if (!validationResult.isValid) {
            const newErrors: Record<string, string> = {};
            validationResult.errors.forEach(error => {
              newErrors[error.parameterName] = error.message;
            });
            setErrors(newErrors);
            return {
              isValid: false,
              errors: newErrors,
            };
          }

          // Transform parameters
          const transformationResult = parameterManager.transformParameters(
            parameters as Record<string, unknown>,
            properties,
            nodeType
          );

          if (transformationResult.errors.length > 0) {
            const newErrors: Record<string, string> = {};
            transformationResult.errors.forEach(error => {
              newErrors[error.parameterName] = error.message;
            });
            setErrors(newErrors);
            return {
              isValid: false,
              errors: newErrors,
            };
          }

          return {
            isValid: true,
            transformed: transformationResult.transformed,
            errors: {},
          };
        },
        clearErrors: () => setErrors({}),
        setIsDirty: (dirty: boolean) => {
          if (!dirty) {
            // Mark that we just saved to prevent immediate form reset
            justSavedRef.current = true;
          }
          setIsDirty(dirty);
        },
      }),
      [
        parameters,
        errors,
        isDirty,
        nodeOperations,
        nodeTypeDefinition,
        parameterManager,
        properties,
        nodeType,
      ]
    );

    if (!nodeTypeDefinition) {
      return (
        <Flex flexDirection="column" gap="4">
          <Text color="fg.error">Unknown node type: {nodeType}</Text>
        </Flex>
      );
    }
    const visibility = parameterManager.resolveParameterVisibility(parameters, properties);

    const handleParameterChange = (propertyName: string, value: NodeParameterValueType) => {
      const newParameters: Record<string, NodeParameterValueType> = {
        ...parameters,
        [propertyName]: value,
      };
      setParameters(newParameters);
      setIsDirty(true);

      if (errors[propertyName]) {
        const { [propertyName]: _, ...newErrors } = errors;
        setErrors(newErrors);
      }

      if (onParameterChange && nodeTypeDefinition && !nodeTypeDefinition.onAsyncSave) {
        const transformationResult = parameterManager.transformParameters(
          newParameters as Record<string, unknown>,
          properties,
          nodeType
        );

        if (transformationResult.errors.length === 0) {
          onParameterChange(transformationResult.transformed);
        }
      }
    };

    if (nodeOperations.isLoadingData) {
      return (
        <Flex flexDirection="column" gap="4" alignItems="center" justifyContent="center" p="8">
          <Spinner size="lg" />
          <Text>Loading configuration...</Text>
        </Flex>
      );
    }

    return (
      <div className={formContainerStyle}>
        {/* Scrollable form content */}
        <div className={formContentStyle}>
          <Flex flexDirection="column" gap="4">
            {properties.map(property => (
              <React.Fragment key={property.name}>
                <PropertyRenderer
                  property={property}
                  value={parameters[property.name]}
                  onChange={value => handleParameterChange(property.name, value)}
                  isVisible={visibility[property.name]}
                  parameters={parameters}
                  error={errors[property.name]}
                />
              </React.Fragment>
            ))}
          </Flex>
        </div>
      </div>
    );
  }
);

NodePropertyForm.displayName = 'NodePropertyForm';
